import React, { useEffect, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { Box, LinearProgress } from '@mui/material';

import useSelector, { useDispatch } from 'components/Booking-new/hooks';
import { resetSearchedLocation, setSearchedLocation, updateState } from 'components/Booking-new/actions/actions';
import stepTypes from 'components/Booking-new/modules/Steps/types';
import { updateURL } from 'components/Booking-new/utils/helpers';
import { getClosestLocationsBySortingAlgo } from 'utils/booking/getClosestLocationsBySortingAlgo';
import isObjectNotEmpty from 'utils/isObjectNotEmpty';
import { flagshipStoreExists, orderByFlagStore } from 'utils/orderByFlagStore';

import { getCountryAlphaCode, getLocationParamKey } from 'components/Booking-new/utils/steps/location';
import { ReactComponent as MagnifyingGlassIcon } from 'mui/assets/icons/MagnifyingGlass.svg';
import {
  StyledInput,
  StyledInputContainer,
  StyledSvgIcon,
  StyledLoaderWrapper,
  StyledInputLoaderOverlay,
} from './styles';

const SearchBox = ({
  placeholder,
  setClosestLocations,
  resetSelectedLocation,
  isMapLoaded,
  getGeoLocation,
  setGotLocations,
  gotLocations,
  isGeoLoading,
  setIsGeoSearched,
  sortingAlgorithm,
}) => {
  const WOOSMAP_CONFIG = {
    cssSrc: 'https://sdk.woosmap.com/localities/style.2.0.css',
    scriptSrc: `https://sdk.woosmap.com/localities/localitieswidget.2.0.js?key=${process.env.GATSBY_WOOSMAP_API_KEY}`,
    search: {
      inputId: 'woosmap-searchbox',
      clearBtnClass: '.localities-empty-button',
    },
    searchConf: {
      components: {
        country: getCountryAlphaCode(),
      },
      customDescription: 'locality:"{name}, {administrative_area_level_1}"',
    },
    events: {
      selectedSuggestion: 'selected_suggestion',
    },
  };

  const { results } = useSelector(state => ({
    userDataSearchedLocation: state?.userData[stepTypes.location]?.selectedLocation?.city,
    selectedCity: state?.selectedCity?.name,
    currentState: state,
  }));
  const dispatch = useDispatch();
  const searchBoxInputRef = useRef(null);
  const searchBoxWrapperRef = useRef(null);
  const [userQuery, setUserQuery] = useState({});
  const [, setInputValue] = useState('');

  const updateClosestLocations = targetLocation => {
    // reset previously selected locations because user searched for new location
    // resetSelectedLocation();
    // get closest locations to searched location
    const closestLocations = getClosestLocationsBySortingAlgo(
      results.currentState?.locations,
      targetLocation,
      sortingAlgorithm,
    );

    // scroll to top of list when updating
    const locationListElement = document.querySelector('.location-list');
    if (locationListElement) locationListElement.scrollTo(0, 0);

    // if any flagship present in the locations list and we are using the old sorting
    if (flagshipStoreExists(closestLocations, sortingAlgorithm))
      return setClosestLocations(orderByFlagStore(closestLocations));

    setClosestLocations(closestLocations);
    setGotLocations(false);
  };

  // Put default userQuery in searchbox input
  useEffect(() => {
    if (!searchBoxInputRef.current || !isObjectNotEmpty(userQuery)) return;

    searchBoxInputRef.current.value = userQuery.formatted_address;
    setInputValue(userQuery.formatted_address);
  }, []);

  // Trigger search action
  // when google map has been loaded and the searchbox contains query from sessionStorage
  useEffect(() => {
    if (!isMapLoaded || !isObjectNotEmpty(userQuery) || searchBoxInputRef.current.value.length === 0) return;

    const targetLocation = userQuery?.targetLocation;
    if (!targetLocation) return;

    updateClosestLocations(targetLocation);
  }, [isMapLoaded]);

  useEffect(() => {
    let localitiesWidget;

    const woosmapScript = document.createElement('script');
    woosmapScript.src = WOOSMAP_CONFIG.scriptSrc;

    document.head.appendChild(woosmapScript);
    const onPlaceChanged = () => {
      const { name, formatted_address, geometry } = localitiesWidget.getSelectedSuggestionDetails();

      const targetLocation = {
        latitude: geometry.location.lat,
        longitude: geometry.location.lng,
      };

      // save user query in state, that later will be saved in sessionStorage
      setUserQuery({ name, formatted_address, targetLocation });
      dispatch(setSearchedLocation({ name, formatted_address, targetLocation }));
      updateClosestLocations(targetLocation);
      setIsGeoSearched(true);
    };

    woosmapScript.onload = () => {
      localitiesWidget = new window.woosmap.localities.Autocomplete(
        WOOSMAP_CONFIG.search.inputId,
        WOOSMAP_CONFIG.searchConf,
      );

      localitiesWidget.addListener(WOOSMAP_CONFIG.events.selectedSuggestion, onPlaceChanged);
    };

    return () => {
      document.head.removeChild(woosmapScript);
      localitiesWidget?.removeListener(WOOSMAP_CONFIG.events.selectedSuggestion, onPlaceChanged);
    };
  }, []);

  const handleInputFocus = ({ target }) => {
    const locationParamKey = getLocationParamKey();

    if (target.value === '' && !locationParamKey && !gotLocations) getGeoLocation();
  };

  const handleInputChange = ({ target }) => {
    const locationParamKey = getLocationParamKey();
    const allowUserToSearchAllLocations = () => {
      const allSteps = results.currentState.allSteps.map(step => {
        if (step.type === stepTypes.location) step.data.locations = results.currentState.locations;

        return step;
      });

      dispatch(
        updateState({
          ...results.currentState,
          allSteps,
        }),
      );

      resetSelectedLocation();
      updateURL(locationParamKey);
    };

    if (target.value === '') {
      resetSelectedLocation();
      dispatch(resetSearchedLocation());
      setGotLocations(false);
      setIsGeoSearched(false);
    } else if (locationParamKey) {
      allowUserToSearchAllLocations();
    }
    setInputValue(target.value);
  };

  const onSearchReset = e => {
    e.preventDefault();
    resetSelectedLocation();
    dispatch(resetSearchedLocation());
    setIsGeoSearched(false);
    const locationListElement = document.querySelector('.location-list');
    if (locationListElement) locationListElement.scrollTo(0, 0);

    return false;
  };

  useEffect(() => {
    if (!searchBoxWrapperRef.current) return;

    const clearButton = searchBoxWrapperRef.current.querySelector(WOOSMAP_CONFIG.search.clearBtnClass);

    if (clearButton) {
      clearButton.style.zIndex = '100';
      clearButton.addEventListener('click', onSearchReset, true);
    }

    return () => {
      clearButton?.removeEventListener('click', onSearchReset, false);
    };
  }, [searchBoxInputRef?.current?.value]);

  return (
    <>
      <Helmet>
        <link rel="stylesheet" href={WOOSMAP_CONFIG.cssSrc} />
      </Helmet>

      <StyledInputContainer ref={searchBoxWrapperRef}>
        <StyledSvgIcon component={MagnifyingGlassIcon} inheritViewBox={true} sx={{ fontSize: 24 }}></StyledSvgIcon>
        <StyledInput
          type="text"
          id={WOOSMAP_CONFIG.search.inputId}
          autoComplete="off"
          placeholder={placeholder || 'City, postal code or location'}
          ref={searchBoxInputRef}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          style={{ borderRadius: 0 }}
          data-test="booking-location-search-input"
          defaultValue={results?.selectedCity || results?.userDataSearchedLocation}
        />
        {isGeoLoading && (
          <>
            <StyledInputLoaderOverlay />
            <StyledLoaderWrapper>
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>
            </StyledLoaderWrapper>
          </>
        )}
      </StyledInputContainer>
    </>
  );
};

export default SearchBox;

SearchBox.propTypes = {
  placeholder: PropTypes.string,
  setClosestLocations: PropTypes.func,
  resetSelectedLocation: PropTypes.func,
  isMapLoaded: PropTypes.bool,
};
